import type { HTMLAttributes } from 'react'
import { cn } from '@/lib/cn'

/**
 * The white surface that sits on the off-white page. No border: the page
 * background is the structure (Kinetic surface/tertiary under surface/primary),
 * with a one-pixel raised shadow so edges still read on low-contrast displays.
 */
export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot="card"
      className={cn(
        'rounded-xl bg-[color:var(--card)] text-[color:var(--card-foreground)] shadow-[var(--shadow-raised)]',
        className,
      )}
      {...props}
    />
  )
}

export function CardHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot="card-header"
      className={cn('flex flex-col gap-1 px-4 pt-4', className)}
      {...props}
    />
  )
}

export function CardTitle({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3
      data-slot="card-title"
      className={cn('text-[13.5px] font-semibold leading-none', className)}
      {...props}
    />
  )
}

export function CardDescription({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p
      data-slot="card-description"
      className={cn('text-[12.5px] text-[color:var(--muted-foreground)]', className)}
      {...props}
    />
  )
}

export function CardContent({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div data-slot="card-content" className={cn('px-4 pb-4', className)} {...props} />
}

export function CardFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot="card-footer"
      className={cn('flex items-center px-4 pb-4', className)}
      {...props}
    />
  )
}
